import React from 'react';
import axios from 'axios';
import { API, Field, Row, TextField, dayLabel, errText, formValues, propName, useHeaders, usePortal, useSubmit } from './kit';

const LABELS = {
  service: 'Service', job_type: 'Visit type', frequency: 'Frequency', property_type: 'Property type', square_footage: 'Size', bedrooms: 'Bedrooms',
  bathrooms: 'Bathrooms', floors: 'Floors / levels', occupancy: 'Occupancy', pets: 'Pets', parking: 'Parking', parking_notes: 'Parking notes',
  primary_goal: 'Main goal', priority_areas: 'Priority areas', focus_areas: 'Areas of focus', focus_notes: 'Focus notes', concerns: 'Concerns',
  someone_home: 'Someone home', entry_instructions: 'Entry instructions', pet_instructions: 'Pet instructions', no_enter_areas: 'Areas not to enter',
  special_surfaces: 'Special surface care', addons: 'Add-ons', condition: 'Current condition', last_cleaned: 'Last professional clean',
  guest_capacity: 'Guest capacity', turnover_frequency: 'Turnover frequency', checkout_time: 'Check-out time', checkin_time: 'Check-in time',
  laundry_handling: 'Laundry', supplies_provided: 'Supplies', facility_type: 'Facility type', facility_sqft: 'Facility size',
  restroom_count: 'Restrooms', scope_areas: 'Scope areas', schedule_frequency: 'Schedule', schedule_window: 'Preferred window', move_direction: 'Move type',
};
const human = (v) => (Array.isArray(v) ? v.map(human).join(', ') : typeof v === 'string' ? v.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : String(v));
export const intakeRows = (data) => Object.entries(LABELS).filter(([k]) => data?.[k] !== undefined && data[k] !== '' && !(Array.isArray(data[k]) && !data[k].length)).map(([k, l]) => [l, human(data[k])]);

export const openIntake = async (headers, addressId, toast) => {
  try {
    const r = await axios.post(`${API}/api/portal/intakes/open`, { address_id: addressId || null }, { headers });
    window.location.assign(r.data.intake_path);
  } catch (e) { toast(errText(e)); }
};

export const IntakeReview = ({ intake, property }) => {
  const headers = useHeaders();
  const { toast } = usePortal();
  return (
    <div data-testid="intake-review">
      <div className="notice">Submitted {dayLabel(intake.submitted_at)} for {propName(property)}. Review the details and update them whenever something changes.</div>
      {intakeRows(intake.data).map(([k, v]) => <Row key={k} k={k} v={v} />)}
      {intake.photo_count > 0 && <Row k="Photos shared" v={intake.photo_count} />}
      <div className="inline-actions"><button className="primary" onClick={() => openIntake(headers, property?.id, toast)} data-testid="intake-update">Update intake</button></div>
    </div>
  );
};

export const PropertyEditForm = ({ property, onSaved }) => {
  const headers = useHeaders();
  const { toast, closeDrawer } = usePortal();
  const [busy, run] = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    const f = formValues(e);
    run(async () => {
      try { await axios.put(`${API}/api/clients/me/addresses/${property.id}`, f, { headers }); toast('Instructions saved. Your team will see them on your next visit.'); closeDrawer(); onSaved(); }
      catch (err) { toast(errText(err)); }
    });
  };
  return (
    <form onSubmit={submit} data-testid="property-form">
      <TextField label="Parking" name="parking_notes" defaultValue={property.parking_notes || ''} maxLength={600} />
      <TextField label="Entry instructions" name="access_notes" defaultValue={property.access_notes || ''} maxLength={600} />
      <Field label="Gate code" name="gate_code" defaultValue={property.gate_code || ''} maxLength={40} />
      <Field label="Lockbox code" name="lockbox_code" defaultValue={property.lockbox_code || ''} maxLength={40} />
      <TextField label="Pets" name="pet_notes" defaultValue={property.pet_notes || ''} maxLength={600} />
      <TextField label="Cleaning priorities" name="cleaning_priorities" defaultValue={property.cleaning_priorities || ''} maxLength={1000} />
      <TextField label="Additional care notes" name="care_notes" defaultValue={property.care_notes || ''} maxLength={1000} />
      <p className="muted">Entry details are shared only with the team assigned to your visits.</p>
      <button className="primary" type="submit" disabled={busy} data-testid="property-save">{busy ? 'Saving…' : 'Save instructions'}</button>
    </form>
  );
};

export const PropertyRequestForm = ({ property, correction, onDone }) => {
  const headers = useHeaders();
  const { toast, closeDrawer } = usePortal();
  const [busy, run] = useSubmit();
  const submit = (e) => {
    e.preventDefault();
    const f = formValues(e);
    run(async (rid) => {
      try {
        await axios.post(`${API}/api/review-items`, { kind: 'property_update', client_request_id: rid, address_id: correction ? property?.id : null,
          title: correction ? `Property correction: ${propName(property)}` : `Additional property: ${f.property}`, details: f.note }, { headers });
        toast('Request sent. Your team will review the property details.'); closeDrawer(); onDone?.();
      } catch (err) { toast(errText(err)); }
    });
  };
  return (
    <form onSubmit={submit} data-testid="property-request-form">
      <Field label="Property name" name="property" defaultValue={correction ? propName(property) : ''} required maxLength={150} readOnly={correction} />
      <TextField label={correction ? 'What needs correcting?' : 'Address and service needs'} name="note" required minLength={3} maxLength={1500} />
      <div className="notice">Your team will review the property details and confirm any changes to service scope.</div>
      <button className="primary" type="submit" disabled={busy} data-testid="property-request-submit">{busy ? 'Sending…' : 'Send request'}</button>
    </form>
  );
};
